import React, {useEffect, useMemo, useRef, useState} from 'react';

import {useStore} from 'effector-react';

import {useTranslation} from 'react-i18next';
import {Column, Hooks, Row, useExpanded, useFlexLayout, usePagination, useTable} from 'react-table';

import {ReactComponent as LeftIcon} from '../../../../../assets/svg/arrow-left.svg';
import {ReactComponent as RightIcon} from '../../../../../assets/svg/arrow-right.svg';

import {ReactComponent as ShowInfo} from '../../../../../assets/svg/edit2.svg';
import {ReactComponent as ExpandArrowIcon} from '../../../../../assets/svg/icon_expand_arrow.svg';

import {I18N_NS} from '../../../../../core/constants/names';
import {$allowedActionsForInvoice} from '../../../../../models/common';
import {
    $invoiceLinesErrors,
    $invoiceLinesHolder,
    copyInvoiceLineData,
    removeInvoiceLinesHolder,
} from '../../../../../models/create';
import {
    $modalViewCodesDropdown,
    setInlineModalFormReadonly,
    setModalDuplicateLinesModal,
} from '../../../../../models/details';
import {colors} from '../../../../../theme';
import {PackageTypes, TRANSPORT_PACKAGE_TYPES} from '../../../../../types/gtins';
import {InvocieLineHolder, InvoiceLine} from '../../../../../types/invoices';

import {isStandartCategory, VAT_CATEGORY} from '../../../../helpers/consts';
import {Icon} from '../../../../ui-kit/Icon';
import {IconButton} from '../../../../ui-kit/IconButton';
import {Input} from '../../../../ui-kit/Input';
import {Select} from '../../../../ui-kit/Select';
import {Tooltip} from '../../../../ui-kit/Tooltip';

import {ModalHandler} from '../../ModalHandler';
import {LineControlComponent} from '../LineControl';

import {
    CellContainer,
    DefaultCell,
    ExpandToggle,
    HeaderCell,
    PageInfo,
    PaginationContainer,
    PaginationControls,
    Root,
    RowDiv,
    RowText,
    RowTextEllipsis,
    TableView,
    TextWrapper,
    TextWrapperItem,
    TextWrapperRow,
} from './styled';

export interface PaginationControlsProps {
    pageIndex: number;
    pageOptions: number[];
    canPreviousPage: boolean;
    canNextPage: boolean;
    pageCount: number;
    gotoPage: (page: number) => void;
    nextPage: () => void;
    previousPage: () => void;
    setPageSize: (size: number) => void;
    pageSize: number;
}

interface SimpleTableProps {
    columns: Column<object>[];
    data: object[];
    cellContainerSettings?: string;
    tableViewExtraSettings?: Record<string, string>;
    hideHeaders?: boolean;
    onPaginationReady?: (paginationProps: PaginationControlsProps) => void;
    updateMyData: (id: string, value: string, depth: {step: number; id: string}) => void;
    skipPageReset: boolean;
    editableCell?: boolean;
    initialState?: {pageIndex: number; pageSize: number};
    emptyDataComponent?: React.ReactNode;
    renderExpandedRow?: boolean;
    showExpandableColumn?: boolean;
    getRowCanExpand?: (row: Row<object>) => boolean;
    expandableColumnId?: string;
    onRowClick?: (row: Row<object>) => void;
    modalParentComponent?: {
        row?: InvocieLineHolder;
        parentIndex?: string;
    };
}

const EditableCell = ({
    value: initialValue,
    row: {index, id: rowId, depth, original, ...rest},
    column: {id},
    updateMyData,
    modalParentComponent,
}: {
    value: string;
    row: {index: number; original: any; id: string; depth: number};
    column: {id: string};
    updateMyData: (id: string, value: string, depth: {step: number; id: string}) => void;
    modalParentComponent?: {
        row?: InvoiceLine;
        parentIndex?: string;
    };
}) => {
    const {t: translation} = useTranslation(I18N_NS);
    const GEN_INFO = 'pages.documents.invoices.create.invoiceLines';
    const t = (key: string) => translation(`${GEN_INFO}.${key}`);
    const allowedActionsForInvoice = useStore($allowedActionsForInvoice);
    const invoiceLinesHolder = useStore($invoiceLinesHolder);
    const [value, setValue] = useState(initialValue);
    const [isEditing, setIsEditing] = useState(false);
    const vatCategoryValue = id === 'vatCategory' ? original?.vatCategory : null;
    const modalViewCodesDropdown = useStore($modalViewCodesDropdown);

    const currentItem = original;
    const {isMarkedItem, copyActions, vatCategory, packagingType} = currentItem;

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
    };

    const currentDepth = useMemo(() => {
        if (modalParentComponent) {
            const {parentIndex} = modalParentComponent;
            const crrentRowIndex = `${parentIndex}.${rowId}`;
            return {step: depth, id: crrentRowIndex};
        }
        return {step: depth, id: rowId};
    }, [depth, rowId]);

    const onSelectChange = (selectedOption: any) => {
        if (id === 'vatCategory' && selectedOption) {
            updateMyData('vatCategory', selectedOption, currentDepth);
            setIsEditing(false);
        }
    };

    const onBlur = () => {
        updateMyData(id, value, currentDepth);
        setIsEditing(false);
    };

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const additionalStyles = `
        text-align: left;
        max-width: 250px;
        border-radius: 8px;
        opacity: 1 !important;
    `;

    const checkLineInfo = (toggleModal: () => void) => (
        <div style={{cursor: 'pointer', marginLeft: 'auto'}}>
            <Icon
                source={ShowInfo}
                width={20}
                height={20}
                fillColor="#FF4B55"
                onClick={() => {
                    toggleModal();
                }}
            />
        </div>
    );

    function wrapObjectToText(item: unknown, lineIndex: string, fieldType: string) {
        if (!item) {
            return '';
        }

        const setText =
            packagingType && TRANSPORT_PACKAGE_TYPES.includes(packagingType)
                ? fieldType === 'description'
                    ? translation(`dictionaries.products.packageType.${packagingType}`)
                    : item
                : item;

        const itemHolderLabel =
            typeof setText === 'string'
                ? setText
                : (setText as {label: string; value: string}).label;

        const tooltipId = `tooltip-${lineIndex}-${fieldType}`;
        return (
            <RowText padding={'0'}>
                <RowTextEllipsis data-tip={itemHolderLabel} data-for={tooltipId}>
                    <Tooltip
                        id={tooltipId}
                        place={'bottom'}
                        backgroundColor="white"
                        textColor="#111111"
                        additionalStyles={
                            additionalStyles + 'box-shadow: 0px 0px 6px rgba(116, 116, 116, 0.3);'
                        }
                    />
                    {itemHolderLabel}
                </RowTextEllipsis>
            </RowText>
        );
    }

    const FIELDS_TO_HIDE = [
        'qty',
        'price',
        'charges',
        'lines',
        'lineAmountWVat',
        'vatCategory',
        'vat',
        'serviceCategory',
    ];
    const cellsToHide = {
        [PackageTypes.BOX]: FIELDS_TO_HIDE,
        [PackageTypes.PALLET]: FIELDS_TO_HIDE,
        [PackageTypes.CONTAINER]: FIELDS_TO_HIDE,
    } as Record<PackageTypes, string[]>;

    const noPadding = useMemo(() => {
        if (id === 'codeGtin') {
            return '0';
        }
        if (value !== '' || id === 'vat') {
            return '0 16px';
        }
        return '0';
    }, [value, id]);

    const dataToDisaply = useMemo(() => {
        if (id === 'lineId') {
            return '';
        }
        // if (id === 'description') {
        //     return wrapObjectToText(original.description, rowId, 'description');
        // }
        // if (id === 'itemCode') {
        //     return wrapObjectToText(original.itemCode, rowId, 'itemCode');
        // }
        if (id === 'vatCategory') {
            return wrapObjectToText(original.vatCategory, rowId, 'vatCategory');
        }
        if (id === 'serviceCategory') {
            return wrapObjectToText(original.serviceCategory, rowId, 'serviceCategory');
        }
        if (id === 'codeGtin') {
            return isMarkedItem ? (
                <TextWrapper>
                    <RowText>{original.code}</RowText>
                    {TRANSPORT_PACKAGE_TYPES.includes(original.packagingType) || (
                        <RowText>{original.gtin}</RowText>
                    )}
                </TextWrapper>
            ) : (
                <TextWrapper>
                    <RowText>{t('unmarked')}</RowText>
                </TextWrapper>
            );
        }
        return value !== '' ? (
            value
        ) : id === 'vat' ? (
            '—'
        ) : (
            <Input
                id={`${id}_${rowId}`}
                size="sm"
                autoFocus={isEditing}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                disabled={id === 'lines' || id === 'lineAmountWVat'}
                placeholder={id === 'lines' || id === 'lineAmountWVat' ? 'Auto' : 'Enter value'}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        onBlur();
                    }
                }}
            />
        );
    }, [index, rowId, id, translation, initialValue, value, original, modalViewCodesDropdown]);

    const fullUneditableFields = useMemo(() => {
        const uneditableFields = [
            'codeGtin',
            'lineId',
            'lines',
            'lineAmountWVat',
            'serviceCategory',
            !isStandartCategory(vatCategory.value) && 'vat',
        ];
        const uneditableFieldsForMarkedItem = ['description', 'qty'];

        return isMarkedItem
            ? [...uneditableFields, ...uneditableFieldsForMarkedItem]
            : uneditableFields;
    }, [invoiceLinesHolder, rowId, id]);

    if (cellsToHide[original.packagingType as PackageTypes]?.includes(id)) {
        return <div />;
    }

    if (id === 'vatCategory' && !vatCategoryValue?.value) {
        return (
            <Select
                id={'vatCategorySelect_1'}
                size="sm"
                value={''}
                options={VAT_CATEGORY}
                onChange={onSelectChange}
                menuPosition={'fixed'}
            />
        );
    }

    if (id === 'control') {
        const {allowToCopy, allowToDuplicateDown, allowToDelete} = copyActions;
        const ACTIONS = [allowToCopy, allowToDuplicateDown, allowToDelete];
        const showMenuButton = ACTIONS.some((action) => action);
        return (
            showMenuButton && (
                <LineControlComponent
                    fieldsLength={invoiceLinesHolder.length}
                    onCopyData={() => copyInvoiceLineData({index: currentDepth.id})}
                    onDuplicateDown={() => {
                        setModalDuplicateLinesModal({index, show: true});
                    }}
                    onRemoveField={() => removeInvoiceLinesHolder(rowId)}
                    allowToCopyData={allowToCopy}
                    allowToDuplicateDown={allowToDuplicateDown}
                    allowToDelete={allowToDelete}
                />
            )
        );
    }

    if (allowedActionsForInvoice.edit && isEditing) {
        if (id === 'vatCategory') {
            return (
                <Select
                    id={'vatCategorySelect'}
                    size="sm"
                    value={vatCategoryValue}
                    options={VAT_CATEGORY}
                    onChange={onSelectChange}
                    menuPosition={'fixed'}
                    onBlur={() => {
                        setIsEditing(false);
                    }}
                    autoFocus={isEditing}
                />
            );
        }
        return (
            <Input
                size="sm"
                autoFocus={isEditing}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={''}
                {...(id !== 'description' && id !== 'itemCode' && {type: 'number', step: '0.01'})}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        onBlur();
                    }
                }}
            />
        );
    }
    return (
        <>
            {id === 'charges' ? (
                !allowedActionsForInvoice.edit ? (
                    original['charges'] ? (
                        <TextWrapperRow>
                            <TextWrapperItem>
                                {original['charges']?.isCharge === 'false' ? '—' : '+'}
                            </TextWrapperItem>
                            <TextWrapperItem>
                                {Number(original['charges']?.amountXaf).toFixed(1)}
                            </TextWrapperItem>
                            {original['charges']?.discountRateAndBaseAmount && (
                                <TextWrapperItem withSeparator={true}>
                                    {`${original['charges']?.ratePercent}%`}
                                </TextWrapperItem>
                            )}
                            {checkLineInfo(() => {
                                setInlineModalFormReadonly({
                                    index: currentDepth.id,
                                    show: true,
                                });
                            })}
                        </TextWrapperRow>
                    ) : (
                        <div style={{padding: '8px 16px'}}>-</div>
                    )
                ) : (
                    <ModalHandler index={currentDepth.id} />
                )
            ) : (
                <DefaultCell
                    noPadding={noPadding}
                    fullUneditableFields={fullUneditableFields as string[]}
                    id={id}
                    allowEditing={allowedActionsForInvoice.edit}
                    role="button"
                    tabIndex={index}
                    onClick={() => {
                        if (!allowedActionsForInvoice.edit) {
                            return;
                        }
                        // const uneditableFields = [
                        //     'codeGtin',
                        //     'lineId',
                        //     'lines',
                        //     'lineAmountWVat',
                        //     'serviceCategory',
                        // ];
                        // const uneditableFieldsForMarkedItem = ['description', 'qty'];

                        // const currentItem = invoiceLinesHolder[index];
                        // const {isMarkedItem} = currentItem;

                        // const fullUneditableFields = isMarkedItem
                        //     ? [...uneditableFields, ...uneditableFieldsForMarkedItem]
                        //     : uneditableFields;

                        if (!fullUneditableFields.includes(id)) {
                            setIsEditing(true);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            onBlur();
                        }
                    }}
                >
                    {dataToDisaply}
                </DefaultCell>
            )}
        </>
    );
};

const renderExpandColumn = (
    showExpandableColumn: boolean,
    expandableColumnId: string | undefined,
    defaultCell: any,
    getRowCanExpand?: (row: Row<object>) => boolean,
) => (hooks: Hooks<object>) => {
    if (!showExpandableColumn) return;
    hooks.visibleColumns.push((cols) => {
        if (expandableColumnId) {
            return cols.map((col) => {
                if (col.id !== expandableColumnId) return col;
                const OriginalCell = col.Cell;
                const CellToUse = OriginalCell ?? defaultCell;
                return {
                    ...col,
                    Cell: (cellProps: any) => {
                        const canExpand = getRowCanExpand ? getRowCanExpand(cellProps.row) : true;
                        if (!canExpand) {
                            return React.createElement(
                                CellToUse as React.ComponentType<any>,
                                cellProps,
                            );
                        }
                        return (
                            <ExpandToggle
                                paddingLeft={`${cellProps.row.depth * 9}px`}
                                {...cellProps.row.getToggleRowExpandedProps()}
                            >
                                <Icon
                                    source={ExpandArrowIcon}
                                    width={24}
                                    height={24}
                                    style={{
                                        transform: `rotate(${
                                            cellProps.row.isExpanded ? '0deg' : '-90deg'
                                        })`,
                                    }}
                                />
                            </ExpandToggle>
                        );
                    },
                };
            });
        }
        return [
            {
                id: 'expander',
                width: 30,
                Header: () => null,
                Cell: ({row}: {row: Row<object>}) => {
                    const canExpand = getRowCanExpand ? getRowCanExpand(row) : true;
                    if (!canExpand) return null;
                    return (
                        <ExpandToggle {...row.getToggleRowExpandedProps()}>
                            <Icon
                                source={ExpandArrowIcon}
                                width={24}
                                height={24}
                                style={{transform: `rotate(${row.isExpanded ? '0deg' : '-90deg'})`}}
                            />
                        </ExpandToggle>
                    );
                },
            },
            ...cols,
        ];
    });
};

export const SimpleTable = ({
    columns,
    data,
    onPaginationReady,
    cellContainerSettings,
    tableViewExtraSettings = {},
    hideHeaders = false,
    updateMyData,
    skipPageReset,
    editableCell = true,
    initialState = {pageIndex: 0, pageSize: 10},
    emptyDataComponent,
    renderExpandedRow = false,
    showExpandableColumn = true,
    getRowCanExpand,
    expandableColumnId,
    onRowClick,
    modalParentComponent,
}: SimpleTableProps) => {
    const defaultColumn = {
        Cell: EditableCell,
    };

    const prevDataRef = useRef(data);

    const isExpandable = renderExpandedRow;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: {pageIndex, pageSize},
        dispatch,
    } = useTable(
        {
            columns,
            data,
            defaultColumn: editableCell ? (defaultColumn as any) : undefined,
            initialState,
            autoResetPage: !skipPageReset,
            updateMyData,
            modalParentComponent,
            autoResetExpanded: false,
        },
        useFlexLayout,
        ...(isExpandable
            ? [
                  useExpanded,
                  renderExpandColumn(
                      showExpandableColumn,
                      expandableColumnId,
                      EditableCell,
                      getRowCanExpand,
                  ),
              ]
            : []),
        usePagination,
    );

    useEffect(() => {
        if (data.length !== prevDataRef.current.length) {
            dispatch({type: 'resetExpanded'});
        }
        prevDataRef.current = data;
    }, [data]);

    useEffect(() => {
        if (onPaginationReady) {
            onPaginationReady({
                pageIndex,
                pageOptions,
                canPreviousPage,
                canNextPage,
                pageCount,
                gotoPage,
                nextPage,
                previousPage,
                setPageSize,
                pageSize,
            });
        }
    }, [
        pageIndex,
        pageOptions,
        canPreviousPage,
        canNextPage,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        pageSize,
        onPaginationReady,
    ]);

    return (
        <Root>
            <TableView tableViewExtraSettings={tableViewExtraSettings} hoverableRow={false}>
                <div {...getTableProps()} className="table">
                    {hideHeaders || (
                        <div className="thead">
                            {headerGroups.map((headerGroup, index) => (
                                <div
                                    {...headerGroup.getHeaderGroupProps()}
                                    className="tr"
                                    key={`headerGroup-${index}`}
                                >
                                    {headerGroup.headers.map((column: any, groupIndex: number) => (
                                        <HeaderCell
                                            {...column.getHeaderProps()}
                                            className="th"
                                            key={`header-${column.id}-${groupIndex}`}
                                        >
                                            {column.render('Header')}
                                        </HeaderCell>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                    <div {...getTableBodyProps()} className="tbody">
                        {page.map((row: any, rowIndex: number) => {
                            prepareRow(row);
                            const errors = $invoiceLinesErrors.getState().find((error) => {
                                const parentIndex = modalParentComponent?.parentIndex;
                                const currentId = parentIndex
                                    ? `${parentIndex.toString()}.${row.id}`
                                    : row.id.toString();
                                const errPath = error.index.toString();
                                return errPath === currentId || errPath.startsWith(`${currentId}.`);
                            });

                            const {packagingType} = row.original;
                            const PACKAGE_TYPES_WITH_MODAL = [
                                PackageTypes.PALLET,
                                PackageTypes.CONTAINER,
                            ];
                            const hasModal =
                                packagingType && PACKAGE_TYPES_WITH_MODAL.includes(packagingType);

                            const canExpand = getRowCanExpand?.(row);

                            return (
                                <RowDiv
                                    {...row.getRowProps()}
                                    key={`row-${rowIndex}`}
                                    className={`tr ${!canExpand && hasModal ? 'has-modal' : ''} ${
                                        errors ? 'has-errors' : ''
                                    }`}
                                    role="row"
                                >
                                    {row.cells.map((cell: any, cellIndex: number) => (
                                        <div
                                            {...cell.getCellProps()}
                                            className="td"
                                            key={`cell-${cellIndex}`}
                                        >
                                            <CellContainer
                                                cellContainerSettings={cellContainerSettings}
                                                onClick={() => {
                                                    const colId = cell.column.id;
                                                    if (
                                                        !hasModal ||
                                                        colId === 'control' ||
                                                        canExpand
                                                    )
                                                        return;

                                                    onRowClick?.(row);
                                                }}
                                            >
                                                {cell.render('Cell')}
                                            </CellContainer>
                                        </div>
                                    ))}
                                </RowDiv>
                            );
                        })}
                    </div>
                    {emptyDataComponent && page.length === 0 && emptyDataComponent}
                </div>
            </TableView>
        </Root>
    );
};

export const SimpleTablePagination = ({
    pageIndex,
    pageOptions,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    pageSize,
}: PaginationControlsProps) => (
    <PaginationContainer>
        <PaginationControls>
            {/* <PaginationButton type="button" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                {'<<'}
            </PaginationButton> */}
            <IconButton
                icon={<Icon source={LeftIcon} width={8} height={13} />}
                disabled={!canPreviousPage}
                color={!canPreviousPage ? colors.textMuted : colors.textPrimary}
                onClick={() => previousPage()}
                hoverIndication
            />
            <PageInfo>
                Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
            </PageInfo>
            <IconButton
                icon={<Icon source={RightIcon} width={8} height={13} />}
                disabled={!canNextPage}
                color={!canNextPage ? colors.textMuted : colors.textPrimary}
                onClick={() => nextPage()}
                hoverIndication
            />
            {/* <PaginationButton
                type="button"
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
            >
                {'>>'}
            </PaginationButton> */}
            {/* <PageSizeSelect
                value={pageSize}
                onChange={(e) => {
                    setPageSize(Number(e.target.value));
                }}
            >
                {[10, 20, 30, 40, 50].map((size) => (
                    <option key={size} value={size}>
                        Показать {size}
                    </option>
                ))}
            </PageSizeSelect> */}
        </PaginationControls>
    </PaginationContainer>
);
